import md5 from 'md5'

import {
  flushAnalyticsEvents,
  Ga4Item,
  queueAnalyticsEvent,
  SeoEvent
} from 'lib/gtm'
import { Cart, collectItemsByCart } from 'modules/cart'
import {
  CustomerTypes,
  getDatalayerPhoneNumber,
  isCustomerAdult} from 'modules/customer'
import { formatPriceWithoutCurrency } from 'modules/products'
import { getNumberOfDayFromDate } from 'shared/utils/date'

import { PlacedSaleOrder,SaleOrder } from '../../types'

interface addPaymentInfoProps {
  user: CustomerTypes | undefined
  cart: Cart | null
  paymentMethodName: string | undefined | null
}

export const addPaymentInfo = ({
  user,
  cart,
  paymentMethodName
}: addPaymentInfoProps) => {
  queueAnalyticsEvent({ ecommerce: null })

  const items: Ga4Item[] = collectItemsByCart(cart?.items || [])

  queueAnalyticsEvent(
    {
      event: SeoEvent.ADD_PAYMENT_INFO, //constant
      type: 'ecommerce', //constant
      user_id: user?.code || undefined, //User ID from crm if logged in, if not we return undefined
      user_adult: isCustomerAdult(user?.dateOfBirth), //1 if user have more than 18 years, 0 - if no, if no value we return undefined
      hashed_email: user?.email ? md5(user?.email) : undefined, //customer email in md5 - if logged in, if not we return undefined
      ecommerce: {
        value: formatPriceWithoutCurrency(cart?.paymentValue.value || 0),
        currency: 'PLN',
        payment_type: paymentMethodName || undefined,
        coupon: cart?.discountCodes[0]?.code || undefined,
        items
      }
    },
    true
  )
}

interface gtmBuyButtonEventProps {
  order: SaleOrder | PlacedSaleOrder
  cart: Cart
  user: CustomerTypes
}

export const gtmBuyButtonEvent = ({
  order,
  cart,
  user
}: gtmBuyButtonEventProps) => {
  const {
    deliveryAddressStreet,
    deliveryAddressCity,
    deliveryAddressPostalCode,
    deliveryAddressHouseNumber,
    deliveryAddressFlatNumber,
    deliverySlot,
    paymentMethod,
    code,
    paymentValue,
    deliveryCostGross,
    totalValueVat
  } = order

  const isUserAdult = isCustomerAdult(user.dateOfBirth)

  const email = md5(user.email).toString()
  const hour = deliverySlot?.start?.substring(0, 2) || ''
  const paymentType = paymentMethod.name as string
  const phoneNumber = getDatalayerPhoneNumber(user.phoneNumber)

  const items = collectItemsByCart(cart.items)

  const dataLayerPayload = {
    event: SeoEvent.PURCHASE, //constant
    type: 'ecommerce', //constant
    user_id: user.code, //User ID from crm if logged in, if not we return undefined
    user_adult: isUserAdult, //1 if user have more than 18 years, 0 - if no, if no value we return undefined
    hashed_email: email, //customer email in md5 - if logged in, if not we return undefined
    delivery_week_day: getNumberOfDayFromDate(deliverySlot?.date || ''), //delivery day of the week - 1 it is monday, 2 tuesday, etc.
    delivery_hour: hour, //delivery hour in 24 hours, starting 00, 01, 02
    payment_type: paymentType, //payment type
    ltv: user?.ltv, //life time value with current purchase
    total_transactions: user?.totalTransactions || undefined, //total transactions client made with current
    new_customer: !!user?.isNewClient, //Information whether the customer has bought from us before. If he has bought then the value is true, if not - false
    last_purchase_date: user?.lastOrder || undefined, //Last time user bought, if new client than undefined
    first_purchase_date: user?.firstOrder || undefined, //First time user bought
    user_data: {
      email: user.email,
      phone_number: phoneNumber,
      address: {
        first_name: user.firstName,
        last_name: user.lastName,
        street: `${deliveryAddressStreet} ${deliveryAddressHouseNumber}${
          deliveryAddressFlatNumber ? `/${deliveryAddressFlatNumber}` : ''
        }`,
        city: deliveryAddressCity,
        region: undefined,
        postal_code: deliveryAddressPostalCode,
        country: 'PL'
      }
    }, //first party data which we can use for google ads enhanced conversions - set undefined if no value
    ecommerce: {
      transaction_id: code, //transaction id
      value: paymentValue.value / 100, //total value paid (with taxes, shipping, etc)
      tax: totalValueVat.value / 100, //taxes - default 0
      shipping: deliveryCostGross.value / 100, //shipping cost - default 0
      currency: 'PLN', //currency,
      coupon: cart?.discountCodes[0]?.code || undefined, //coupon if exists - if not undefined
      coupon_value: cart?.discountCodesTotalValue.value / 100 || undefined, //coupon if exists - if not undefined
      items
    }
  }

  queueAnalyticsEvent({ ecommerce: null }, true)
  queueAnalyticsEvent(dataLayerPayload, true)
}

export const eventsPurchaseAnalytics = ({
  user,
  cart,
  paymentMethodName,
  order
}: {
  user: CustomerTypes | undefined
  cart: Cart
  paymentMethodName: string
  order: SaleOrder | PlacedSaleOrder
}) => {
  addPaymentInfo({
    user,
    cart,
    paymentMethodName
  })

  gtmBuyButtonEvent({ order, cart, user: user as CustomerTypes })

  flushAnalyticsEvents()

  return new Promise(resolve => {
    setTimeout(() => {
      resolve(true)
    }, 200)
  })
}
